/**
 * Dates as the Feature Activation screens say them.
 *
 * ## Why not `lib/time.ts`
 *
 * `relativeTime` answers "how long ago" for an audit feed, and does it with
 * `Intl.RelativeTimeFormat`: "yesterday", "2 weeks ago", "in 3 hours". These
 * screens count **calendar days** — "Today", "Yesterday", "12 days ago" — because
 * what an operator reads off them is "when did the museum last look", and a
 * visit at 23:50 is yesterday at 00:10 whatever the hour count says. Weeks
 * would also blur exactly the distinction the list is sorted on. `shortDate` is
 * pinned to `en-GB` ("25 Sept 2026"); the design is `en-US` ("Sep 25, 2026").
 *
 * Every function takes `now` so the tests can pin it; the screens pass nothing.
 *
 * An absent or unparseable value becomes an em dash (or nothing, for the grey
 * line under it): a missing date here means "never happened", and `Invalid Date`
 * would read as a bug in this app rather than as that.
 */

export const DASH = '—';

const MS_PER_DAY = 86_400_000;

function parse(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Midnight, local time — the day a timestamp falls on for the operator. */
function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Whole calendar days between the timestamp and `now`, never negative.
 *
 * A timestamp slightly in the future is clock skew between the museum's
 * browser, the hub and this one, not a visit from tomorrow — so it is today.
 * `Math.round` rather than `floor` absorbs the 23- and 25-hour days a DST
 * change produces.
 */
export function daysAgo(iso: string, now: Date = new Date()): number | null {
    const date = parse(iso);
    if (!date) return null;
    return Math.max(0, Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY));
}

/** "Today", "Yesterday", "12 days ago" — or "—" when there is no date. */
export function relativeDay(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return DASH;
    const days = daysAgo(iso, now);
    if (days === null) return DASH;
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${String(days)} days ago`;
}

/** "Sep 25, 2026" — or "" when there is no date (it only ever sits under a relative one). */
export function absoluteDay(iso: string | null | undefined): string {
    const date = parse(iso);
    return date
        ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
}

/** "as of Sep 25" — the freshness line under a museum's status; "" when it never visited. */
export function asOfLabel(iso: string | null | undefined): string {
    const date = parse(iso);
    return date
        ? `as of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : '';
}
